import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { useLocalStorageState, useSessionStorageState } from "ahooks";
import * as React from "react";
import { useQuery } from "react-query";

import {
  FrontendSettings,
  SystemApi,
  ActionCode,
  UserApi,
  UserProfileDto,
} from "../api";
import { baseUrl } from "../apiConfig";
import { useRouter } from "../hooks";
import { GetUserLoginNameFromSpGlobgalContext } from "../utils/sharepointHelperFunctions";

interface IAuthProviderProps {
  children: React.ReactNode;
}

export interface ErrorDetails {
  statusCode?: number;
  message?: string | null;
}

function getMsalConfig(feSettings: FrontendSettings): Configuration {
  return {
    auth: {
      clientId: feSettings.clientId,
      authority: feSettings.authority,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message, containsPII) => {
          // eslint-disable-next-line no-console
          console.log("[MSAL]", message);
        },
        piiLoggingEnabled: false,
        logLevel: LogLevel.Warning,
      },
    },
  };
}

const AuthContext = React.createContext<IAuthContext>(null);

export const AuthProvider: React.FunctionComponent<IAuthProviderProps> = ({
  children,
}) => {
  const [logoutReason, setLogoutReason] = React.useState<string>();

  const [user, setUser] = React.useState<UserProfileDto>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [loginError, setLoginError] = React.useState();

  const [azureAdAccount, setAzureAdAccount] = React.useState<AccountInfo>();

  const [, setRedirectUri] = useSessionStorageState<string>("redirect_uri");
  const [lastAccountName, setLastAccountName] =
    useLocalStorageState<string>("account_name");

  const finalLastAccountName = React.useMemo(
    () => GetUserLoginNameFromSpGlobgalContext() || lastAccountName,
    [lastAccountName]
  );

  const { location } = useRouter();

  const logout = React.useCallback(
    async (reason?: string) => {
      setLogoutReason(reason);
      await msalInstance.logoutRedirect({
        account: azureAdAccount,
      });
      setUser(null);
    },
    [azureAdAccount]
  );

  const systemApi = React.useMemo(
    () =>
      new SystemApi({
        basePath: baseUrl,
        middlewares: [],
      }),
    []
  );
  const { data: feSettings } = useQuery("frontend-settings", () =>
    systemApi.frontendSettings()
  );

  const msalInstance = React.useMemo(() => {
    if (feSettings) {
      return new PublicClientApplication(getMsalConfig(feSettings));
    } else {
      return null;
    }
  }, [feSettings]);

  const authenticate = React.useCallback(async () => {
    setAzureAdAccount(null);
    const previousPath = location.pathname;
    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse !== null) {
      setAzureAdAccount(redirectResponse.account);
      setLastAccountName(redirectResponse.account.username);
    } else {
      setRedirectUri(previousPath);
      const account = msalInstance.getAccountByUsername(finalLastAccountName);
      if (account) {
        console.log("Local account found ...", account);

        setAzureAdAccount(account);
      } else {
        console.log("No account found, login with azure ad ! ");

        await msalInstance.loginRedirect({
          scopes: [feSettings?.scope],
          loginHint: finalLastAccountName,
        });
      }
    }
  }, [
    feSettings,
    finalLastAccountName,
    location.pathname,
    msalInstance,
    setLastAccountName,
    setRedirectUri,
  ]);

  const isLoggedIn = React.useMemo(() => !!user, [user]);

  React.useEffect(() => {
    if (!msalInstance) return;
    if (azureAdAccount) return;
    authenticate().then();
  }, [authenticate, azureAdAccount, isLoggedIn, msalInstance]);

  const getAccessToken = React.useCallback(async () => {
    let refreshedToken: AuthenticationResult;
    try {
      if (azureAdAccount) {
        refreshedToken = await msalInstance.acquireTokenSilent({
          account: azureAdAccount,
          scopes: [feSettings?.scope],
        });
        return "Bearer " + refreshedToken.accessToken;
      }
    } catch (error) {
      console.error("Unable to get token silently", error);
      await logout();
    }

    return null;
  }, [azureAdAccount, feSettings?.scope, msalInstance, authenticate]);

  React.useEffect(() => {
    if (azureAdAccount) {
      console.log("Azure ad account ready, getting user profile...");

      const api = new UserApi({
        basePath: baseUrl,
        middlewares: [],
        apiKeyPromise: getAccessToken,
      });

      api
        .profile()
        .then((user) => {
          setUser(user);
          setIsLoading(false);
        })
        .catch((error) => {
          setLoginError(error);
          setIsLoading(false);
        });
    }
  }, [azureAdAccount, getAccessToken]);

  const hasAction = React.useCallback(
    (actionList: ActionCode[] = []) => {
      if (actionList.length === 0) return true;
      return actionList.filter((v) => user?.actions.includes(v)).length > 0;
    },
    [user?.actions]
  );

  const fullUserName = React.useMemo(
    () => azureAdAccount?.name,
    [azureAdAccount?.name]
  );

  return (
    <AuthContext.Provider
      value={{
        error: loginError,
        hasAction,
        // isCreator,
        isLoading,
        logout,
        logoutReason,
        user,
        getAccessToken,
        isLoggedIn,
        fullUserName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface IAuthContext {
  logout: () => void;
  isLoading: boolean;
  user?: UserProfileDto;
  error?: ErrorDetails;
  hasAction: (actionList: ActionCode[]) => boolean;
  logoutReason: string;
  // isCreator: <T extends ICreatedByType>(
  //   actionList: ActionCode[],
  //   data: T
  // ) => boolean;
  getAccessToken: () => Promise<string>;
  isLoggedIn: boolean;
  fullUserName: string;
}

export const useAuth = () => React.useContext(AuthContext);
